<template>
  <div class="trix-start">
    <input id="trix" type="hidden" :name="name" :value="value">
    <trix-editor placeholder="Write your reply..." ref="trix" input="trix"></trix-editor>

    <div v-if="preSavedResponses && screenWidth > 767">
      <tooltip data-track="open saved replies" v-if="billingDetails && canShowSavedRepliesIcon" :ref-elem-id="this.savedResponses.btnId" :is-popover="true"
               placement="top-right" trigger="click" ref="savedReplies">
        <div class="f14" v-if="!savedResponses.showForm">
          <div class="is-clearfix padding-10">
            <strong class="is-pulled-left">Saved replies</strong>
            <a data-track="create a saved reply" href="#" class="color-2aabe1 is-pulled-right" v-if="savedResponsesCount < 5" @click.prevent="showSavedResponseForm">Create</a>
          </div>
          <div class="is-divider"></div>
          <p v-if="!savedResponsesCount" class="padding-10 margin-top-70 has-text-centered">There are no saved replies.</p>
          <div v-else v-for="(savedResponse, index) in savedResponses.data">
            <div class="padding-10">
              <div class="is-pulled-right opts">
                <a href="#" class="has-text-black" @click.prevent="deleteSavedResponse(index)"><i class="fas fa-trash"></i></a>
              </div>
              <div data-track="select saved reply" class="is-clipped pointer" @click="useSavedResponse(savedResponse.content)">
                <strong>{{ savedResponse.title }}</strong>
                <p>{{ savedResponse.content }}</p>
              </div>
            </div>
            <div class="is-light-divider"></div>
          </div>
        </div>
        <div class="f14" v-else>
          <div class="is-clearfix padding-10">
            <strong class="is-pulled-left">Create saved reply</strong>
          </div>
          <div class="is-divider"></div>
          <form class="padding-10" @submit.prevent="createSavedResponse">
            <div class="field">
              <div class="control" :class="{'is-loading': savedResponses.submittingForm}">
                <input :disabled="savedResponses.submittingForm" class="input f14" type="text" placeholder="Title of saved reply" v-model="savedResponses.form.title">
              </div>
              <p class="help is-danger" v-if="savedResponses.form.errors.has('title')" v-text="savedResponses.form.errors.get('title')"></p>
            </div>
            <div class="field">
              <div class="control" :class="{'is-loading': savedResponses.submittingForm}">
                <textarea :disabled="savedResponses.submittingForm" class="textarea f14" placeholder="Write your saved reply here"
                          v-model="savedResponses.form.content"></textarea>
              </div>
              <p class="help is-danger" v-if="savedResponses.form.errors.has('content')" v-text="savedResponses.form.errors.get('content')"></p>
            </div>
            <div class="is-clearfix p-y-10">
              <button class="button is-button-large is-button-blue-outline" @click="showSavedResponseList">
                CANCEL
              </button>
              <button data-track="created a new saved reply" class="button is-button-large is-button-blue is-pulled-right">
                SAVE
              </button>
            </div>
          </form>
        </div>
      </tooltip>
      <tooltip v-else :ref-elem-id="this.savedResponses.btnId" placement="top-right" :trigger="['click', 'hover']">Saved replies are only available on our Premium plan.
      </tooltip>
    </div>
  </div>
</template>

<script>
/* eslint-disable indent,no-new,no-undef */
import Trix from 'trix'
import Tooltip from '../widgets/Tooltip'
import ApiResource from '../api/ApiResource'
import misc from '../mixins/misc.js'

export default {
  data () {
    return {
      savedResponses: {
        btnId: '',
        showForm: false,
        data: [],
        submittingForm: false,
        form: new ApiResource({})
      },
      billingDetails: null
    }
  },
  computed: {
    savedResponsesCount () {
      return this.savedResponses.data.length
    },
    canShowSavedRepliesIcon () {
      const allowedPlans = ['P1', 'P2', 'P3', 'P4', 'Pro', 'Enterprise']
      return this.preSavedResponses && this.screenWidth > 767 && this.billingDetails !== null && (allowedPlans.indexOf(this.billingDetails.subscription.name) !== -1)
    }
  },
  mixins: [misc],
  name: 'wysiwyg',
  components: {
    Trix,
    Tooltip
  },
  props: {
    name: {
      type: String
    },
    value: {
      type: String
    },
    shouldClear: {
      type: [Boolean, Number]
    },
    preSavedResponses: {
      type: [Boolean, Number]
    },
    simpleEditor: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    shouldClear () {
      this.$refs.trix.value = ''
    }
  },
  created () {
    const localBillingDetails = localStorage.getItem('billingDetails')
    if (typeof localBillingDetails !== 'undefined' && localBillingDetails !== null) {
      this.billingDetails = JSON.parse(localBillingDetails)
      const allowedPlans = ['P1', 'P2', 'P3', 'P4', 'Pro', 'Enterprise']
      if (this.preSavedResponses && this.screenWidth > 767 && (allowedPlans.indexOf(this.billingDetails.subscription.name) !== -1)) {
        window.events.$on('tooltipShown', refElemId => {
          if (refElemId === this.savedResponses.btnId) {
            this.savedResponses.form.send(`/${localStorage.org_id}/saved-responses`, {}, 'get')
              .then(data => {
                this.savedResponses.data = data
              })
          }
        })
      }
    }
  },
  mounted () {
    this.$refs.trix.addEventListener('trix-change', e => {
      this.$emit('input', e.target.value)

      const simpleText = e.target.editor.getDocument().toString()
      this.$emit('charLength', simpleText.trim())
    })

    if (this.simpleEditor) {
      this.$refs.trix.addEventListener('paste', e => {
        e.preventDefault()
        e.stopImmediatePropagation()
        this.$refs.trix.editor.insertString(e.clipboardData.getData('text'))
      }, true)
    }

    this.$refs.trix.addEventListener('trix-initialize', e => {
      const btnGroup = e.target.toolbarElement.querySelector('.trix-button-group.trix-button-group--text-tools')

      if (!this.simpleEditor) {
        // add Unordered List to first button group
        const bulletList = e.target.toolbarElement.querySelector('.trix-button.trix-button--icon.trix-button--icon-bullet-list')
        btnGroup.appendChild(bulletList)

        // add Numbered List to first button group
        const numbList = e.target.toolbarElement.querySelector('.trix-button.trix-button--icon.trix-button--icon-number-list')
        btnGroup.appendChild(numbList)

        e.target.toolbarElement.querySelector('.trix-button.trix-button--icon.trix-button--icon-bold').className += ' trixBtn'
        e.target.toolbarElement.querySelector('.trix-button.trix-button--icon.trix-button--icon-italic').className += ' trixBtn'
        bulletList.className += ' trixBtn'
        numbList.className += ' trixBtn'
      } else {
        e.target.toolbarElement.querySelector('.trix-button.trix-button--icon.trix-button--icon-bold').remove()
        e.target.toolbarElement.querySelector('.trix-button.trix-button--icon.trix-button--icon-italic').remove()
      }

      // delete second and third button group
      e.target.toolbarElement.querySelector('.trix-button-group.trix-button-group--block-tools').remove()
      e.target.toolbarElement.querySelector('.trix-button-group.trix-button-group--history-tools').remove()

      // remove unwanted buttons from the group
      e.target.toolbarElement.querySelector('.trix-button.trix-button--icon.trix-button--icon-strike').remove()
      e.target.toolbarElement.querySelector('.trix-button.trix-button--icon.trix-button--icon-link').remove()

      // styling toolbar
      e.target.toolbarElement.querySelector('.trix-button-row').style.backgroundColor = '#e7e5e5'
      e.target.toolbarElement.querySelector('.trix-button-row').style.height = '29px'

      // add margin top to toolbar and remove button borders
      btnGroup.style.marginTop = '10px'
      btnGroup.style.border = 'none'
      btnGroup.style.width = '100%'

      if (this.preSavedResponses && this.screenWidth > 767) {
        // add pre defined replies button
        const btn = document.createElement('button')
        const btnId = 'tooltip-' + Date.now() + Math.random()
        btn.type = 'button'
        btn.id = btnId
        btn.setAttribute('class', 'trix-button trix-button--icon trixBtn')
        btn.setAttribute('style', 'text-indent: initial;margin-left: auto')

        let icon = '<i class="fas fa-comment-slash fa-lg"></i>'
        const allowedPlans = ['P1', 'P2', 'P3', 'P4', 'Pro', 'Enterprise']
        if (this.billingDetails !== null && (allowedPlans.indexOf(this.billingDetails.subscription.name) !== -1)) {
          icon = '<i class="far fa-comment-dots fa-lg"></i>'
        } else {
          icon = '<i class="fas fa-comment-slash fa-lg"></i>'
        }
        btn.innerHTML = icon

        btnGroup.appendChild(btn)
        this.savedResponses.btnId = btnId
      }
    })
  },
  destroyed () {
    window.events.$off('tooltipShown')
  },
  methods: {
    /**
     * Show saved responses create form
     *
     * @returns {void}
     */
    showSavedResponseForm () {
      this.savedResponses.showForm = true
    },
    /**
     * Show saved responses list
     *
     * @returns {void}
     */
    showSavedResponseList () {
      this.savedResponses.showForm = false
    },
    /**
     * Create new saved response
     *
     * @returns {void}
     */
    createSavedResponse () {
      this.savedResponses.submittingForm = true
      this.savedResponses.form.send(`/${localStorage.org_id}/saved-responses`, {
        title: this.savedResponses.form.title,
        content: this.savedResponses.form.content
      })
        .then(() => {
          this.savedResponses.submittingForm = false
          this.savedResponses.data.unshift({id: this.savedResponses.form.id, title: this.savedResponses.form.title, content: this.savedResponses.form.content})
          this.savedResponses.showForm = false
          this.savedResponses.form.title = ''
          this.savedResponses.form.content = ''
        })
        .catch(() => {
          this.savedResponses.submittingForm = false
        })
    },
    /**
     * Delete a saved response
     *
     * @param {integer} index Index of record in data
     *
     * @returns {void}
     */
    deleteSavedResponse (index) {
      const data = this.savedResponses.data[index]
      this.savedResponses.form.send(`/${localStorage.org_id}/saved-responses/${data.id}`, {}, 'delete')
        .then(() => {
          this.savedResponses.data.splice(index, 1)
        })
    },
    /**
     * Use a saved response
     *
     * @param {string} content Saved response content
     *
     * @returns {void}
     */
    useSavedResponse (content) {
      this.$refs.trix.value = content
      this.$refs.savedReplies.hideTooltip()
    }
  }
}
</script>

<style>
trix-toolbar .trix-button.trixBtn {
  border: none;
  color: #8a8c91;
}

trix-editor {
  background-color: #fff;
  border-radius: 0;
  border: none;
  color: #8a8c91;
}

trix-editor ul {
  list-style-type: none;
  margin: 0;
  padding: 0;
}

trix-editor ol {
  margin: 0;
  padding: 0;
  list-style-position: inside;
}

trix-editor ul li, trix-editor ol li {
  margin: 0;
  padding: 0;
}

trix-editor ul li::before {
  content: '•';
  margin-right: 10px;
}

.trix-start {
  margin-top: 1rem;
  margin-bottom: 1rem;
  border: 1px solid #8a8c91;
}

.is-divider {
  border-top: 1px solid #8a8c91
}

.is-light-divider {
  border-top: 1px solid #dbdbdd
}

.tooltip .opts {
  width: 15px;
}
</style>



// WEBPACK FOOTER //
// src/components/Wysiwyg.vue